import {useCallback, useEffect} from 'react'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AgendaSidebarCard} from '@/features/agenda/components/agenda-sidebar-card/AgendaSidebarCard'
import {AgendaButtonFilterSelection} from '@/features/agenda/components/agenda-button-filter-selection/AgendaButtonFilterSelection'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {AgendaSubject} from '@/features/agenda/types'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {
    StyledAgendaSidebarViewCardScrollArea,
    StyledAgendaSidebarViewContent
} from '@/features/agenda/components/agenda-sidebar-view-card/style'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {Divider} from '@/components/ui/divider/Divider'
import {AvailableDatesButton} from '@/features/agenda/components/available-dates-button/AvailableDatesButton'
import {useAgendaCategoryTeam} from '@/features/agenda/hooks/useAgendaCategoryTeam'
import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'
import {AgendaSidebarCardLoader} from '@/features/agenda/components/agenda-sidebar-card/AgendaSidebarCardLoader'
import {Text} from '@/components/ui/text/Text'
import {formatDate} from '@/dayjs'

export const AgendaSidebarTeamCard = () => {
    const {t} = useTranslation()
    const {searchParams, setSearchParams, isSubjectSelected, clearSearchParams} = useAgendaSearchParams()
    const {team, patient, isLoading, isSuccess, projectTeam} = useAgendaCategoryTeam()

    const {
        navigateToView,
        params: {entity}
    } = useAgendaNavigation()

    const handleClickTitle = () => {
        setSearchParams({
            category: 'team',
            patientId: patient.id,
            subjectIds: []
        })
    }

    const handleClickSubject = (subject: AgendaSubject) => {
        setSearchParams({
            category: 'team',
            patientId: patient.id,
            subjectIds: searchParams.subjectIds?.includes(subject.id)
                ? searchParams.subjectIds?.filter(id => id != subject.id)
                : [...(searchParams.subjectIds || []), subject.id]
        })
    }

    const onRemoveTeam = useCallback(() => {
        navigateToView('week')
        clearSearchParams()
    }, [navigateToView, clearSearchParams])

    useEffect(() => {
        if (entity == 'users' && patient && team.length == 0 && isSuccess) {
            toast.error(
                t('agenda:no_team_found', {
                    fullName: patient.fullName
                })
            )
        }
    }, [entity, patient, team, isSuccess])

    const mainTeam = team.filter(teamMember => !teamMember.isAReserve)
    const reserveTeam = team.filter(teamMember => teamMember.isAReserve)

    if (isLoading) {
        return <AgendaSidebarCardLoader title={t('agenda:team_loading')} />
    }

    if (searchParams.category != 'team') {
        return null
    }

    return (
        <AgendaSidebarCard
            title={t('agenda:equipe_name', {fullName: patient?.fullName})}
            subtitle={
                projectTeam &&
                t('projects:code_date', {
                    code: projectTeam?.code,
                    date: projectTeam?.startDate && formatDate(projectTeam.startDate, 'DD/MM/YYYY')
                })
            }
            onClickTitle={handleClickTitle}
            isSelected={searchParams.category == 'team'}
            onRemove={onRemoveTeam}
        >
            <StyledAgendaSidebarViewCardScrollArea scrollbar={<Scrollbar />}>
                <StyledAgendaSidebarViewContent gap={1} direction="column" fullWidth>
                    {team.length > 0 ? (
                        <>
                            {mainTeam.map(teamMember => (
                                <AgendaButtonFilterSelection
                                    key={teamMember.id}
                                    isSelected={isSubjectSelected(teamMember.id)}
                                    onClick={() => handleClickSubject(teamMember)}
                                >
                                    <Avatar name={teamMember.fullName} size={'xs'} />
                                    <Flexbox gap={1} align="center" fullWidth>
                                        <p style={{width: '100%'}}>{teamMember.fullName}</p>
                                        {isSubjectSelected(teamMember.id) && <XCloseIcon />}
                                    </Flexbox>
                                </AgendaButtonFilterSelection>
                            ))}

                            {reserveTeam.length > 0 && (
                                <>
                                    <Text size="textSm" palette="neutral" shade="500">
                                        {t('projects:team_reserve')}
                                    </Text>
                                    {reserveTeam.map(teamMember => (
                                        <AgendaButtonFilterSelection
                                            key={teamMember.id}
                                            isSelected={isSubjectSelected(teamMember.id)}
                                            onClick={() => handleClickSubject(teamMember)}
                                        >
                                            <Avatar name={teamMember.fullName} size={'xs'} />
                                            <Flexbox gap={1} align="center" fullWidth>
                                                <p style={{width: '100%'}}>{teamMember.fullName}</p>
                                                {isSubjectSelected(teamMember.id) && <XCloseIcon />}
                                            </Flexbox>
                                        </AgendaButtonFilterSelection>
                                    ))}
                                </>
                            )}
                        </>
                    ) : (
                        <Text size="textSm" palette="neutral" shade="500">
                            {t('commons:no_results')}
                        </Text>
                    )}
                </StyledAgendaSidebarViewContent>
            </StyledAgendaSidebarViewCardScrollArea>
            {team.length > 0 && (
                <>
                    <Divider direction="horizontal" />
                    <AvailableDatesButton />
                </>
            )}
        </AgendaSidebarCard>
    )
}
